import { Link, router } from '@inertiajs/react'
import {
  ArrowLeft,
  ExternalLink,
  FileText,
  CreditCard,
  Download,
} from 'lucide-react'
import AppLayout from '@/layouts/AppLayout'
import StatusBadge from '@/components/StatusBadge'
import AuditTimeline from '@/components/AuditTimeline'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { formatCurrency, formatDate } from '@/lib/utils'
import type { PurchaseOrder, PurchaseOrderStatus } from '@/types'

interface Props {
  purchase_order: PurchaseOrder
}

const allStatuses: PurchaseOrderStatus[] = ['draft', 'in_progress', 'estimated', 'finalized']

const statusLabels: Record<PurchaseOrderStatus, string> = {
  draft: 'Draft',
  in_progress: 'In Progress',
  estimated: 'Estimated',
  finalized: 'Finalized',
}

export default function PurchaseOrderShow({ purchase_order: po }: Props) {
  const isFinalized = po.status === 'finalized'
  const availableTransitions = isFinalized
    ? []
    : allStatuses.filter((s) => s !== po.status)

  function handleTransition(newStatus: PurchaseOrderStatus) {
    router.patch(`/purchase_orders/${po.id}/transition`, { status: newStatus })
  }

  return (
    <AppLayout>
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Button asChild variant="ghost" size="icon-sm">
          <Link href="/purchase_orders">
            <ArrowLeft className="size-4" />
          </Link>
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold tracking-tight text-foreground">
              Purchase Order #{po.id}
            </h1>
            <StatusBadge status={po.status} />
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Created {formatDate(po.created_at)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild variant="outline" size="sm">
            <a href={`/purchase_orders/${po.id}/pdf`} target="_blank">
              <Download className="size-4" />
              PDF
            </a>
          </Button>
          {!isFinalized && (
            <Button asChild variant="outline" size="sm">
              <Link href={`/purchase_orders/${po.id}/edit`}>Edit</Link>
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left column - Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Details card */}
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="text-base">Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-3">
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">Amount</p>
                  <p className="text-2xl font-bold font-mono text-foreground">
                    {formatCurrency(parseFloat(po.amount))}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">Date</p>
                  <p className="text-sm text-foreground">{formatDate(po.date)}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">Status</p>
                  <StatusBadge status={po.status} />
                </div>
              </div>

              {po.google_sheet_url && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">Google Sheet</p>
                  <a
                    href={po.google_sheet_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 transition-colors"
                  >
                    <ExternalLink className="size-3.5" />
                    Open spreadsheet
                  </a>
                </div>
              )}

              {po.notes && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">Notes</p>
                  <p className="text-sm text-foreground whitespace-pre-wrap">{po.notes}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Status transitions */}
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="text-base">Status Transition</CardTitle>
            </CardHeader>
            <CardContent>
              {isFinalized ? (
                <p className="text-sm text-muted-foreground">
                  This purchase order has been finalized and cannot be changed.
                </p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {availableTransitions.map((status) => (
                    <Button
                      key={status}
                      variant="outline"
                      size="sm"
                      onClick={() => handleTransition(status)}
                    >
                      Move to {statusLabels[status]}
                    </Button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Linked payments */}
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <CreditCard className="size-4 text-muted-foreground" />
                Linked Payments
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!po.payments || po.payments.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">
                  No payments linked to this purchase order
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-border/50 hover:bg-transparent">
                      <TableHead className="text-xs text-muted-foreground">Amount</TableHead>
                      <TableHead className="text-xs text-muted-foreground">Date</TableHead>
                      <TableHead className="text-xs text-muted-foreground">Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {po.payments.map((payment) => (
                      <TableRow key={payment.id} className="border-border/30">
                        <TableCell className="font-mono text-sm font-medium">
                          {formatCurrency(parseFloat(payment.amount))}
                        </TableCell>
                        <TableCell className="text-sm">
                          {formatDate(payment.date)}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {payment.notes || '--'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right column - Change history */}
        <div>
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <FileText className="size-4 text-muted-foreground" />
                Change History
              </CardTitle>
            </CardHeader>
            <CardContent>
              <AuditTimeline logs={po.audit_logs || []} />
            </CardContent>
          </Card>
        </div>
      </div>
    </AppLayout>
  )
}
